/** Floating captions' model: what to say over which cell after an action, and their expiry. */
import type { GameEvent, GameState } from '@brig/rules-engine';
import { useCallback, useEffect, useRef, useState } from 'react';
import { reducedMotion, shotLandsAfter } from '../board/ballistics.ts';

export interface Caption {
  readonly id: number;
  readonly cell: string;
  readonly text: string;
  readonly tone: 'good' | 'bad' | 'plain';
}

/** A caption to show, after `delay` ms: a shot's result once its ball has fallen. */
export type NewCaption = Omit<Caption, 'id'> & { readonly delay?: number };

/** How long a caption stays, ms. */
export const CAPTION_MS = 2200;

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Captions for one action's events, placed on the cells they concern in `after`. */
export function captionsFor(
  t: Translate,
  events: readonly GameEvent[],
  before: GameState,
  after: GameState,
): NewCaption[] {
  const cellOf = (s: GameState, id: string) =>
    s.ships.find((x) => x.id === id)?.cell ?? s.forts.find((f) => f.id === id)?.cell ?? null;
  const out: NewCaption[] = [];
  const still = reducedMotion();
  for (const e of events) {
    if (e.type === 'shot') {
      const cell = cellOf(before, e.targetId);
      if (!cell) continue;
      const ship = after.ships.find((s) => s.id === e.targetId);
      const fort = after.forts.find((f) => f.id === e.targetId);
      let text = t('game.fire.miss');
      if (e.hit && ship?.sunk) text = t('game.caption.sunk');
      else if (e.hit && ship)
        text = `${t('game.fire.hit')} ${ship.damage}/${after.ruleset.shipClasses[ship.cls].sinksAt}`;
      else if (e.hit && fort?.destroyed) text = t('game.caption.destroyed');
      else if (e.hit && fort)
        text = `${t('game.fire.hit')} ${fort.hits}/${after.ruleset.fortClasses[fort.cls].destroyedAt}`;
      const own =
        after.ships.find((s) => s.id === e.targetId)?.side ??
        after.forts.find((f) => f.id === e.targetId)?.side;
      out.push({
        cell,
        text,
        tone: !e.hit ? 'plain' : own === before.turn.side ? 'bad' : 'good',
        delay: still ? 0 : shotLandsAfter(e.shooters.length),
      });
    } else if (e.type === 'shipMoved' && e.mode === 'port') {
      out.push({ cell: e.to, text: t('game.caption.portMoved'), tone: 'plain' });
    } else if (e.type === 'shipWrecked') {
      out.push({ cell: e.cell, text: t('game.caption.wrecked'), tone: 'bad' });
    } else if (e.type === 'shipRepaired') {
      const cell = cellOf(after, e.shipId);
      if (cell) out.push({ cell, text: t('game.caption.repaired', { n: e.damage }), tone: 'good' });
    }
  }
  return out;
}

/**
 * A list of captions that come after their delay and expire on their own. A new caption on a
 * cell takes the place of the one there, so two never stack on one unit.
 */
export function useCaptions(): [readonly Caption[], (items: readonly NewCaption[]) => void] {
  const [captions, setCaptions] = useState<readonly Caption[]>([]);
  const next = useRef(1);
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending) clearTimeout(timer);
    };
  }, []);
  const add = useCallback((items: readonly NewCaption[]) => {
    const later = (ms: number, run: () => void) => {
      const timer = setTimeout(() => {
        timers.current.delete(timer);
        run();
      }, ms);
      timers.current.add(timer);
    };
    for (const { delay = 0, ...item } of items) {
      later(delay, () => {
        const id = next.current++;
        setCaptions((cs) => [...cs.filter((c) => c.cell !== item.cell), { ...item, id }].slice(-6));
        later(CAPTION_MS, () => {
          setCaptions((cs) => cs.filter((c) => c.id !== id));
        });
      });
    }
  }, []);
  return [captions, add];
}
